import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import PopoverPortal from './PopoverPortal';

export interface SingleSelectOption<T extends string | number = string> {
  value: T;
  label: string;
}

export interface SingleSelectDropdownProps<T extends string | number = string> {
  label?: string;
  options: SingleSelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
}

export function SingleSelectDropdown<T extends string | number = string>({
  label,
  options,
  value,
  onChange,
  ariaLabel,
}: SingleSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayText = selectedOption?.label ?? label ?? '';

  const selectByIndex = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) return;
      onChange(option.value);
      setIsOpen(false);
      setFocusedIndex(-1);
      buttonRef.current?.focus();
    },
    [options, onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          event.preventDefault();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(Math.max(options.findIndex((o) => o.value === value), 0));
          } else {
            setFocusedIndex((prev) => Math.min(prev + 1, options.length - 1));
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => Math.max(prev - 1, 0));
          }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(Math.max(options.findIndex((o) => o.value === value), 0));
          } else if (focusedIndex >= 0) {
            selectByIndex(focusedIndex);
          }
          break;
        case 'Home':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(0);
          }
          break;
        case 'End':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(options.length - 1);
          }
          break;
        case 'Tab':
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
          }
          break;
      }
    },
    [isOpen, focusedIndex, options, value, selectByIndex],
  );

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, isOpen]);

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setFocusedIndex(Math.max(options.findIndex((o) => o.value === value), 0));
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel ?? (label ? `${label}: ${displayText}` : displayText)}
        className="flex items-center justify-between gap-2 px-3 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-primary hover:border-brand-primary/50 transition-colors"
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown
          className={`w-4 h-4 text-ui-text-tertiary transition-transform flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <PopoverPortal
        anchorRef={containerRef}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setFocusedIndex(-1);
        }}
        width={224}
        innerRef={listRef}
        className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg max-h-64 overflow-y-auto"
      >
        <div role="listbox" aria-label={label ?? ariaLabel} className="p-2 space-y-1">
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => selectByIndex(index)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between gap-2 ${
                    isSelected
                      ? 'bg-brand-primary/10 text-brand-primary'
                      : 'text-ui-text-primary hover:bg-ui-background-tertiary'
                  } ${focusedIndex === index ? 'ring-2 ring-brand-primary ring-inset' : ''}`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
                </button>
              );
            })}
        </div>
      </PopoverPortal>
    </div>
  );
}

export default SingleSelectDropdown;
